/**
 * The duel wire format (AD-008).
 *
 * Compact JSON rather than a binary codec: the whole per-tick payload is five
 * small integers, so the saving from packing them into bytes is a few hundred
 * bytes a second on a channel that is already carrying SCTP framing — and a
 * readable frame is worth far more than that when a desync has to be
 * diagnosed from a log.
 *
 * Three properties matter more than size and all three are enforced here:
 *
 * - **Every frame carries `v`.** A peer running a different protocol build is
 *   rejected at the handshake instead of desyncing 40 seconds into a fight.
 * - **Every frame that decides the fight carries `engine`.** `v` is a
 *   hand-maintained integer and therefore says nothing about the *simulation*:
 *   it sat at 1 through 57 commits to `packages/engine`'s flight and combat
 *   code, any one of which makes two builds fly the same stick differently.
 *   `DuelSetup.engine` carries `engineFingerprint()` — a measured identity that
 *   moves whenever the model does — and `hello` puts it on the wire before a
 *   duel can be agreed at all.
 * - **Inputs are quantised on the way *in*, not just on the way out.**
 *   `canonicalInputs` is `decodeInputs(encodeInputs(x))`, and the lockstep core
 *   files the local player's own stick through it before simulating with it.
 *   Without that the sender would step on the raw float and the receiver on the
 *   rounded one, which is a divergence the hash would catch and nothing would
 *   explain.
 */

import {
  clamp,
  COMBAT_LEVELS,
  NEUTRAL_INPUTS,
  type AircraftKey,
  type CombatLevel,
  type FlightInputs,
  type FlightModelTier,
} from "@red-opus/engine";

/**
 * Bumped to 2 by the build-gate lane: `DuelSetup` gained three required fields
 * (`engine`, `hashInterval`, `combatLevel`) and `hello` joined the message set,
 * so a v1 peer's `setup` frame is not one this build may fly.
 */
export const NET_PROTOCOL_VERSION = 2;

/** Axis quantisation: −1000..+1000, so a stick position costs at most five characters. */
export const AXIS_SCALE = 1000;

const FIRING_BIT = 1;
const CLEAR_JAM_BIT = 2;
/**
 * The bomb toggle. A duel has no bombs to drop, so this bit is never set in the
 * one thing this protocol currently carries — it is here because
 * `canonicalInputs` has to be a *complete* round-trip of `FlightInputs` or the
 * sender would step on a field the receiver never saw, which is precisely the
 * unexplainable divergence the module note above exists to prevent. Adding a
 * bit an old peer never sets and a new peer never reads in a duel does not
 * change any frame on the wire, so `NET_PROTOCOL_VERSION` stands.
 */
const BOMB_RELEASE_BIT = 4;

/** `[pitch, roll, yaw, throttle, flags]`, all integers. */
export type EncodedInputs = readonly [number, number, number, number, number];

export function encodeInputs(inputs: FlightInputs): EncodedInputs {
  return [
    Math.round(clamp(inputs.pitch, -1, 1) * AXIS_SCALE),
    Math.round(clamp(inputs.roll, -1, 1) * AXIS_SCALE),
    Math.round(clamp(inputs.yaw, -1, 1) * AXIS_SCALE),
    Math.round(clamp(inputs.throttle, 0, 1) * AXIS_SCALE),
    (inputs.firing ? FIRING_BIT : 0) |
      (inputs.clearJam ? CLEAR_JAM_BIT : 0) |
      (inputs.bombRelease ? BOMB_RELEASE_BIT : 0),
  ];
}

export function decodeInputs(encoded: EncodedInputs): FlightInputs {
  const [pitch, roll, yaw, throttle, flags] = encoded;
  return {
    pitch: pitch / AXIS_SCALE,
    roll: roll / AXIS_SCALE,
    yaw: yaw / AXIS_SCALE,
    throttle: throttle / AXIS_SCALE,
    firing: (flags & FIRING_BIT) !== 0,
    clearJam: (flags & CLEAR_JAM_BIT) !== 0,
    bombRelease: (flags & BOMB_RELEASE_BIT) !== 0,
  };
}

/**
 * The exact value both peers will simulate with. Idempotent, which is what
 * makes it safe for the sender to record its own filing through it.
 */
export function canonicalInputs(inputs: FlightInputs): FlightInputs {
  return decodeInputs(encodeInputs(inputs));
}

/**
 * Everything both peers need to build the identical encounter. Host-authored,
 * sent once per duel, and **the only source of truth for anything the
 * simulation reads**. Nothing here may be re-derived from a local setting on
 * the receiving side: a duel between a player who picked NOVICE and one who
 * picked ACE is a duel between two different fights, diverging on tick 1 and
 * surfacing as an unexplained desync a second later.
 *
 * What that covers, field by field: `seed` (which also draws the duel's wind,
 * see `duel-setup.ts`'s `duelWeather`), `tier` (which *is* the realism set —
 * `REALISM_PRESETS[tier]`), both aeroplanes, the input delay the two ledgers
 * prefill from, the hash schedule the integrity check runs on, and the
 * Realism Panel's COMBAT LEVEL, which no duel reads today and which is here
 * anyway so that the day one does, it reads the negotiated one.
 */
export interface DuelSetup {
  readonly seed: number;
  readonly tier: FlightModelTier;
  readonly host: AircraftKey;
  readonly guest: AircraftKey;
  /** Ticks of input delay agreed for this duel (`DEFAULT_INPUT_DELAY` unless tuned). */
  readonly inputDelay: number;
  /** Ticks between hash exchanges (`DEFAULT_HASH_INTERVAL` unless tuned) — negotiated so both peers check the same ticks. */
  readonly hashInterval: number;
  /** The host's COMBAT LEVEL, stamped on the encounter rather than left to each side's own panel. */
  readonly combatLevel: CombatLevel;
  /** The host's `engineFingerprint()`. A guest whose own differs must refuse the duel, not fly it. */
  readonly engine: string;
}

export const DUEL_END_REASONS = ["decided", "quit", "desync"] as const;
export type DuelEndReason = (typeof DUEL_END_REASONS)[number];

export type DuelMessage =
  /**
   * Both ways, first frame on the open channel: who this peer *is*, in the only
   * sense a lockstep duel cares about — which simulation it runs.
   */
  | { readonly t: "hello"; readonly v: number; readonly engine: string }
  /** Guest → host, in the lobby: which aeroplane this player has picked. */
  | { readonly t: "pick"; readonly v: number; readonly aircraft: AircraftKey }
  /** Host → guest: the agreed duel. Receiving it is what starts the fight. */
  | { readonly t: "setup"; readonly v: number; readonly setup: DuelSetup }
  /** Guest → host: encounter built, stepping. */
  | { readonly t: "ready"; readonly v: number }
  /** A run of consecutive ticks starting at `tick` — see `INPUT_REDUNDANCY`. */
  | { readonly t: "inputs"; readonly v: number; readonly tick: number; readonly runs: readonly EncodedInputs[] }
  | { readonly t: "hash"; readonly v: number; readonly tick: number; readonly hash: string }
  | { readonly t: "end"; readonly v: number; readonly reason: DuelEndReason };

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function asEncodedInputs(value: unknown): EncodedInputs | null {
  if (!Array.isArray(value) || value.length !== 5) return null;
  if (!value.every((entry) => typeof entry === "number" && Number.isFinite(entry))) return null;
  const [pitch, roll, yaw, throttle, flags] = value as [number, number, number, number, number];
  return [pitch, roll, yaw, throttle, flags];
}

function asInputsMessage(raw: Record<string, unknown>, version: number): DuelMessage | null {
  const { tick, runs } = raw;
  if (typeof tick !== "number" || !Number.isInteger(tick) || tick < 0) return null;
  if (!Array.isArray(runs) || runs.length === 0) return null;
  const decoded: EncodedInputs[] = [];
  for (const entry of runs) {
    const inputs = asEncodedInputs(entry);
    if (!inputs) return null;
    decoded.push(inputs);
  }
  return { t: "inputs", v: version, tick, runs: decoded };
}

function asSetupMessage(raw: Record<string, unknown>, version: number): DuelMessage | null {
  const setup = raw.setup;
  if (!isRecord(setup)) return null;
  const { seed, tier, host, guest, inputDelay, hashInterval, combatLevel, engine } = setup;
  if (typeof seed !== "number" || !Number.isInteger(seed)) return null;
  if (typeof tier !== "string" || typeof host !== "string" || typeof guest !== "string") return null;
  if (typeof inputDelay !== "number" || !Number.isInteger(inputDelay) || inputDelay < 0) return null;
  if (typeof hashInterval !== "number" || !Number.isInteger(hashInterval) || hashInterval < 0) return null;
  if (!COMBAT_LEVELS.includes(combatLevel as CombatLevel)) return null;
  if (typeof engine !== "string" || engine.length === 0) return null;
  return {
    t: "setup",
    v: version,
    setup: {
      seed,
      tier: tier as FlightModelTier,
      host: host as AircraftKey,
      guest: guest as AircraftKey,
      inputDelay,
      hashInterval,
      combatLevel: combatLevel as CombatLevel,
      engine,
    },
  };
}

function asHashMessage(raw: Record<string, unknown>, version: number): DuelMessage | null {
  const { tick, hash } = raw;
  if (typeof tick !== "number" || !Number.isInteger(tick) || tick < 0) return null;
  if (typeof hash !== "string" || hash.length === 0) return null;
  return { t: "hash", v: version, tick, hash };
}

/**
 * `hello` is parsed **outside the version gate**, on purpose: it is the frame
 * that says which build is talking, so a peer whose `v` we would otherwise
 * refuse is exactly the peer we most need to hear from. It keeps the sender's
 * own `v` rather than ours, so the receiver can tell a same-protocol build
 * difference from a different-protocol one.
 */
function asHelloMessage(raw: Record<string, unknown>): DuelMessage | null {
  if (raw.t !== "hello") return null;
  if (typeof raw.v !== "number" || !Number.isInteger(raw.v)) return null;
  if (typeof raw.engine !== "string" || raw.engine.length === 0) return null;
  return { t: "hello", v: raw.v, engine: raw.engine };
}

function asSimpleMessage(raw: Record<string, unknown>, version: number): DuelMessage | null {
  if (raw.t === "ready") return { t: "ready", v: version };
  if (raw.t === "pick" && typeof raw.aircraft === "string") {
    return { t: "pick", v: version, aircraft: raw.aircraft as AircraftKey };
  }
  if (raw.t === "end" && DUEL_END_REASONS.includes(raw.reason as DuelEndReason)) {
    return { t: "end", v: version, reason: raw.reason as DuelEndReason };
  }
  return null;
}

export function encodeMessage(message: DuelMessage): string {
  return JSON.stringify(message);
}

/**
 * Parses one frame, or returns `null` for anything that is not a well-formed
 * message of this protocol version.
 *
 * **A `null` is evidence, not noise.** This channel carries nothing but frames
 * this game's own code wrote, so a frame this build cannot parse identifies a
 * peer that is not this build. `duel-connection.ts` treats the first one as a
 * build mismatch and says so; letting it fall through would leave the lobby
 * waiting for a peer whose every frame is going to be dropped, which is the
 * one thing a player cannot diagnose. It is also the only way a peer old
 * enough to predate `hello` gets told anything at all.
 */
export function decodeMessage(frame: string): DuelMessage | null {
  let raw: unknown;
  try {
    raw = JSON.parse(frame);
  } catch {
    return null;
  }
  if (!isRecord(raw)) return null;
  const hello = asHelloMessage(raw);
  if (hello) return hello;
  if (raw.v !== NET_PROTOCOL_VERSION) return null;
  const version = NET_PROTOCOL_VERSION;
  if (raw.t === "inputs") return asInputsMessage(raw, version);
  if (raw.t === "setup") return asSetupMessage(raw, version);
  if (raw.t === "hash") return asHashMessage(raw, version);
  return asSimpleMessage(raw, version);
}

/** A filing with nothing pressed — what both peers pre-load the delay window with. */
export const NEUTRAL_ENCODED: EncodedInputs = encodeInputs(NEUTRAL_INPUTS);
